import React, { useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import Modal from '../../modules/Modal';
import { getUserOrganization } from '../../../api';
import deleteModalStyles from '../../VideofeedsList/DeleteVideofeedModal/DeleteVideofeedModal.module.scss';
import { useAppDispatch } from '../../../store/hooks';
import { deleteFolderById, getFolderDeleteImpact } from '../../../api/foldersApi';
import { SortOptions, SortType } from '../../../enums/SORT';
import { saveMyOrganization } from '../../../store/user/userSlice';
import { getFoldersThunk } from '../../../store/folders/foldersSlice';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';

type TDeleteFolderModal = {
  open: boolean;
  switchModal: () => void;
  folderId: string;
  data: { page: number; limit: number; search?: string; sort?: SortType; sortOption: SortOptions };
  onDeleted?: () => void;
};

const DeleteFolderModal: React.FC<TDeleteFolderModal> = ({ open, switchModal, folderId, data, onDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactedComposers, setImpactedComposers] = useState<string[]>([]);
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const onModalClose = () => {
    switchModal();
  };
  const dispatch = useAppDispatch();

  const handleLocalStorageKeysForResumeUploading = () => {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        if (key && key.startsWith('=videofeedToResume=') && key.includes(`=${folderId}::`)) {
          console.log('2');
          localStorage.removeItem(key);
        }
      }
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      handleLocalStorageKeysForResumeUploading();
      await deleteFolderById(folderId);
      if (onDeleted) {
        onDeleted();
      } else {
        dispatch(getFoldersThunk(data));
      }

      getUserOrganization()
        .then((resp) => {
          dispatch(saveMyOrganization(resp.data));
        })
        .catch((error) => console.error(error));
    } catch (err) {
      console.log(err);
    } finally {
      onModalClose();
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    if (!open || !folderId) return;

    setImpactLoading(true);
    setImpactedComposers([]);

    getFolderDeleteImpact(folderId)
      .then((response) => {
        if (!active) return;
        const names = Array.isArray(response?.data) ? response.data : [];
        setImpactedComposers(
          [...new Set(names.map((name: string) => String(name || '').trim()).filter(Boolean))],
        );
      })
      .catch(() => {
        if (active) setImpactedComposers([]);
      })
      .finally(() => {
        if (active) setImpactLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, folderId]);

  useHotkeys('Enter', () => {
    if (open && !loading) submitButtonRef?.current?.click();
  });

  return (
    <Modal open={open} onClose={onModalClose} loading={loading}>
      <div className={deleteModalStyles.modalBody}>
        <div className={deleteModalStyles.modalTitle}>Are you sure you want to delete this folder?</div>
        <div className={deleteModalStyles.restoreDisclaimer}>
          Deleted folders can be restored for 30 days from Deleted folders.
        </div>
        {!impactLoading && !!impactedComposers.length && (
          <>
            <div className={deleteModalStyles.modalSubtitleSecondary}>
              Deleting this folder will also remove clips from these Online Composers. Composers that only depend on this source will be deleted:
            </div>
            <div className={deleteModalStyles.namesList}>
              {impactedComposers.map((name) => (
                <div key={name} className={deleteModalStyles.nameItem}>
                  {name}
                </div>
              ))}
            </div>
          </>
        )}
        <div className={deleteModalStyles.actionBtns}>
            <OutlinedButton onClick={handleDelete} kind="default" disabled={loading || impactLoading}>
              <div ref={submitButtonRef}>Yes, I am sure</div>
            </OutlinedButton>

            <ContainedButton kind="purple" onClick={onModalClose} disabled={loading || impactLoading}>
              No, cancel
            </ContainedButton>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteFolderModal;
